import { useEffect, useState } from 'react';
import type { LiberacaoCondicaoResponse, LiberacaoTabelaResponse, LiberacaoUfCondicaoResponse, LiberacaoUfResponse, UfsPraticadasResponse } from '@triade/contracts';
import { UFS } from '@triade/contracts';
import { usePode } from '@/api/me';
import { useSalvarUfsPraticadas, useUfsPraticadas } from '@/api/ufs-praticadas';
import { FaixaLucroBadge, ProfitBadge } from '@/components/profit-badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { fraseDerivacaoUf } from './tipos';

const CHIP = 'rounded-full px-2 text-caption leading-5 font-medium whitespace-nowrap';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/** Gravacao de uma celula da grade por UF: vai para PUT /precificacao/rascunho com `uf` (preco null = voltar a derivar). */
export type GravarUf = (linha: { tabela: LiberacaoTabelaResponse; uf: string; contribuinte: boolean; condicaoId: string; preco: string | null }) => Promise<void>;

/**
 * Sessao 51b: "Preço por UF" (so em tabela com `porUf`). Uma linha por UF praticada (sem a de origem, que e a grade normal) x
 * uma coluna por condicao. Nada e calculado aqui: preco, selo, lucro e faixa vem de GET /precificacao/liberacao/item/:id.
 */
export function PrecoPorUf({ tabela, onGravar, pendente }: { tabela: LiberacaoTabelaResponse; onGravar: GravarUf; pendente: boolean }) {
  const { pode } = usePode();
  const podeEditarUfs = pode('tabela.editar');
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const porUf = tabela.porUf;
  if (!porUf) return null;
  const ufs = porUf.ufs;

  return (
    <section aria-label={`Preço por UF — ${tabela.tabelaNome}`} data-testid="preco-por-uf" className="flex flex-col gap-2 rounded-xl border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-body font-semibold">Preço por UF</h4>
          <p className="mt-0.5 text-caption text-ink-muted">
            {fraseDerivacaoUf(porUf.derivacao, porUf.ufOrigem)} Digite um preço para fixar uma UF (manual).
          </p>
        </div>
        {podeEditarUfs && (
          <Button type="button" size="sm" variant="outline" aria-haspopup="dialog" onClick={() => setDialogoAberto(true)}>
            UFs praticadas
          </Button>
        )}
      </div>

      {ufs.length === 0 ? (
        <p className="text-small text-ink-muted">
          Nenhuma UF praticada além da origem.
          {podeEditarUfs && ' Use "UFs praticadas" para incluir.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-215 border-collapse text-small">
            <thead>
              <tr className="border-b border-border text-left text-caption text-ink-muted">
                <th scope="col" className="px-2 py-2 font-medium">
                  UF
                </th>
                {tabela.condicoes.map((c) => (
                  <th key={c.condicaoId} scope="col" className="px-2 py-2 font-medium">
                    {c.condicaoNome}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ufs.map((u) => (
                <LinhaUf key={u.uf} uf={u} tabela={tabela} onGravar={onGravar} pendente={pendente} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {podeEditarUfs && <UfsPraticadasDialog tabelaId={tabela.tabelaId} tabelaNome={tabela.tabelaNome} aberto={dialogoAberto} onAberto={setDialogoAberto} />}
    </section>
  );
}

function LinhaUf({ uf, tabela, onGravar, pendente }: { uf: LiberacaoUfResponse; tabela: LiberacaoTabelaResponse; onGravar: GravarUf; pendente: boolean }) {
  return (
    <tr data-testid="linha-uf" data-uf={uf.uf} className="border-b border-border align-top">
      <th scope="row" className="px-2 py-2 text-left font-semibold">
        {uf.uf}
        {!uf.contribuinte && <span className={cn(CHIP, 'ml-1.5 bg-secondary text-ink-muted')}>não contribuinte</span>}
      </th>
      {tabela.condicoes.map((c) => (
        <CelulaUf
          key={c.condicaoId}
          uf={uf}
          condicao={c}
          celula={uf.condicoes.find((x) => x.condicaoId === c.condicaoId)}
          tabela={tabela}
          onGravar={onGravar}
          pendente={pendente}
        />
      ))}
    </tr>
  );
}

function CelulaUf({
  uf,
  condicao,
  celula,
  tabela,
  onGravar,
  pendente,
}: {
  uf: LiberacaoUfResponse;
  condicao: LiberacaoCondicaoResponse;
  celula: LiberacaoUfCondicaoResponse | undefined;
  tabela: LiberacaoTabelaResponse;
  onGravar: GravarUf;
  pendente: boolean;
}) {
  const rascunho = celula?.rascunho ?? null;
  const [texto, setTexto] = useState(() => decimalParaTextoCampo(rascunho));
  const [erroCampo, setErroCampo] = useState<string | null>(null);
  // Depois de gravar (ou de outra acao na tela), o campo volta a refletir o rascunho da API.
  useEffect(() => setTexto(decimalParaTextoCampo(rascunho)), [rascunho]);

  if (!celula) return <td className="px-2 py-2 text-ink-disabled">—</td>;

  const manual = celula.efetivoOrigem === 'MANUAL';
  const rotuloOrigem = manual ? 'Manual' : celula.derivadoDe ? `Derivado de ${celula.derivadoDe}` : 'Derivado';
  const idCampo = `uf-${tabela.tabelaId}-${uf.uf}-${condicao.condicaoId}`;

  function salvar() {
    const preco = decimalDigitado(texto);
    if (preco === null) {
      setErroCampo('Informe um valor como 25,00.');
      return;
    }
    setErroCampo(null);
    void onGravar({ tabela, uf: uf.uf, contribuinte: uf.contribuinte, condicaoId: condicao.condicaoId, preco });
  }

  return (
    <td data-testid="celula-uf" data-origem={celula.efetivoOrigem ?? undefined} className="px-2 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="tabular font-semibold">{formatarMoeda(celula.efetivo)}</span>
        {celula.efetivoOrigem && <span className={cn(CHIP, manual ? 'bg-warning-bg text-warning-ink' : 'bg-secondary text-ink-muted')}>{rotuloOrigem}</span>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {celula.lucro === null ? (
          <span className="text-ink-muted">—</span>
        ) : tabela.lucroDesejado ? (
          <ProfitBadge compacto lucro={celula.lucro} meta={tabela.lucroDesejado} minimo={tabela.lucroMinimo} faixa={celula.faixa ?? undefined} />
        ) : (
          <span className="flex items-center gap-1.5">
            <span className="tabular">{formatarFracaoPercentual(celula.lucro)}</span>
            {celula.faixa && <FaixaLucroBadge faixa={celula.faixa} />}
          </span>
        )}
      </div>
      {rascunho !== null && <p className="tabular mt-1 text-caption font-semibold text-primary">Rascunho: {formatarMoeda(rascunho)}</p>}
      <p className="tabular text-caption text-ink-muted">Publicado: {formatarMoeda(celula.publicado)}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <input
          id={idCampo}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          aria-label={`Fixar preço ${uf.uf} ${condicao.condicaoNome}`}
          placeholder="fixar (manual)"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          aria-invalid={erroCampo ? true : undefined}
          className="tabular h-8 w-28 rounded-md border border-input bg-card px-2 outline-none focus-visible:border-primary focus-visible:shadow-focus"
        />
        <Button type="button" size="sm" variant="outline" disabled={pendente} onClick={salvar}>
          Salvar no rascunho
        </Button>
        {manual && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pendente}
            onClick={() => {
              setErroCampo(null);
              void onGravar({ tabela, uf: uf.uf, contribuinte: uf.contribuinte, condicaoId: condicao.condicaoId, preco: null });
            }}
          >
            voltar a derivar
          </Button>
        )}
      </div>
      {erroCampo && (
        <p role="alert" className="mt-1 text-caption text-danger-ink">
          {erroCampo}
        </p>
      )}
    </td>
  );
}

/** "UFs praticadas" (`tabela.editar`): as 27 UFs marcaveis; a de origem sempre marcada e travada; sugestao (origem, clientes). */
function UfsPraticadasDialog({ tabelaId, tabelaNome, aberto, onAberto }: { tabelaId: string; tabelaNome: string; aberto: boolean; onAberto: (v: boolean) => void }) {
  return (
    <Dialog open={aberto} onOpenChange={onAberto}>
      <DialogContent className="max-h-[90vh] max-w-[560px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>UFs praticadas</DialogTitle>
          <DialogDescription>
            {tabelaNome}: as UFs de destino que recebem preço por UF. A UF de origem fica sempre marcada.
          </DialogDescription>
        </DialogHeader>
        {/* Dentro do conteudo (so montado com o dialogo aberto): a consulta so roda quando o usuario abre. */}
        <ConteudoUfs tabelaId={tabelaId} onFechar={() => onAberto(false)} />
      </DialogContent>
    </Dialog>
  );
}

function ConteudoUfs({ tabelaId, onFechar }: { tabelaId: string; onFechar: () => void }) {
  const consulta = useUfsPraticadas(tabelaId);
  if (consulta.isError) return <p role="alert" className="text-small text-danger-ink">{mensagemErro(consulta.error)}</p>;
  if (!consulta.data) return <p role="status" className="text-small text-ink-muted">Carregando UFs praticadas…</p>;
  return <FormUfs tabelaId={tabelaId} dados={consulta.data} onSalvo={onFechar} onCancelar={onFechar} />;
}

function FormUfs({ tabelaId, dados, onSalvo, onCancelar }: { tabelaId: string; dados: UfsPraticadasResponse; onSalvo: () => void; onCancelar: () => void }) {
  const salvar = useSalvarUfsPraticadas(tabelaId);
  const origem = dados.ufOrigem;
  // Estado inicial = as que valem hoje (efetivas) + a origem, que e sempre marcada.
  const [marcadas, setMarcadas] = useState<Set<string>>(() => new Set([...dados.efetivas, ...(origem ? [origem] : [])]));
  const [erro, setErro] = useState<string | null>(null);
  const sugestao = new Map(dados.sugeridas.map((s) => [s.uf, s.motivo]));

  function marcar(uf: string, ligado: boolean) {
    setMarcadas((antes) => {
      const depois = new Set(antes);
      if (ligado) depois.add(uf);
      else depois.delete(uf);
      return depois;
    });
  }

  async function onSalvar() {
    setErro(null);
    try {
      await salvar.mutateAsync({ ufs: UFS.filter((u) => marcadas.has(u)) });
      onSalvo();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <>
      <p className="text-caption text-ink-muted">
        {dados.salvas === null ? 'Ainda não foi escolhida: vale a sugestão (origem e UFs dos clientes com orçamento).' : 'Escolha salva pela empresa.'}
      </p>
      <fieldset className="grid grid-cols-3 gap-x-4 gap-y-2 sm:grid-cols-4">
        <legend className="sr-only">UFs praticadas</legend>
        {UFS.map((u) => {
          const motivo = sugestao.get(u);
          return (
            <div key={u} className="flex items-center gap-2 text-small">
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={marcadas.has(u)}
                  disabled={u === origem}
                  onChange={(e) => marcar(u, e.target.checked)}
                  className="size-4 accent-primary"
                />
                {u}
              </label>
              {motivo && <span className="text-caption text-ink-muted">{motivo === 'ORIGEM' ? 'origem' : 'clientes'}</span>}
            </div>
          );
        })}
      </fieldset>
      {erro && (
        <p role="alert" className="text-small text-danger-ink">
          {erro}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="button" disabled={salvar.isPending} onClick={() => void onSalvar()}>
          Salvar UFs
        </Button>
      </DialogFooter>
    </>
  );
}
